import { ASnackbar } from '@adornis/adornis-components/a-snackbar';
import { AdornisRouter } from '@adornis/base/client/a-router';
import type { Maybe } from '@adornis/base/utilTypes';
import OptimisticObservable from '@adornis/baseql/optimisticObservable';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/x-flex';
import { EntityValidator } from '@adornis/forms/entity-validator';
import '@adornis/forms/x-form';
import '@adornis/forms/x-radiogroup';
import '@adornis/germanium/components/ge-button';
import '@adornis/germanium/components/ge-country-picker';
import '@adornis/germanium/components/ge-date-picker-dropdown';
import GermaniumDialog from '@adornis/germanium/components/ge-dialog';
import '@adornis/germanium/components/ge-dropdown-search-selection';
import '@adornis/germanium/components/ge-dropdown-selection';
import '@adornis/germanium/components/ge-input';
import '@adornis/germanium/components/ge-radiogroup';
import '@adornis/germanium/components/ge-section';
import '@adornis/germanium/components/ge-toggle-button';

import { geSpacing } from '@adornis/germanium/constants';
import '@adornis/internationalization/client/language-picker/x-country-picker';
import '@adornis/popover/x-dropdown-search-selection';

import '@adornis/datetime/x-date-picker-dropdown';
import { AdornisForm } from '@adornis/forms/x-form';
import { universityList } from 'db/verified-domains-list';
import { EDUCATION, UserCreationEntity, WTGUser } from 'db/wtg-user';
import { html } from 'lit';
import { nothing } from 'lit-html';
import { customElement, state } from 'lit/decorators.js';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import '../components/wtg-blog-post-preview';

@customElement('wtg-create-profile')
export class WTGCreateProfile extends ChemistryLitElement {
  @state() size = new SizeController(this);

  @state() user = new ObservableController(this, WTGUser.currentUser as OptimisticObservable<Maybe<WTGUser>>);
  @state() currStep = new ObservableController(this, AdornisRouter.get('step'));

  @state() public userCreationDoc = new ObservableController(
    this,
    this.user.observable.pipe(map(user => (!user?.userData ? new UserCreationEntity({}) : user.userData))),
  );

  styles = () => ({ ':host': { backgroundColor: 'white', height: '100%' } });

  @state() public userCreationChanging: ObservableController<UserCreationEntity> = new ObservableController(
    this,
    this.userCreationDoc.observable.pipe(
      filter(Boolean),
      switchMap(user => user.whenChanging),
      // tap(osce => logger.debug({ taskType: JSON.stringify(osce) }, 'OSCE')),
      // debounceTime(10),
    ),
  );

  private steps = ['name', 'country', 'education', 'study_details'];

  firstUpdated() {
    AdornisRouter.get()
      .queryParams.get('loginToken')
      .pipe(takeUntil(this.disconnected), filter(Boolean), take(1))
      .subscribe(val => {
        // alert(val);
        if (val) WTGUser.loginWithToken(val);
      });
  }

  public render() {
    if (!this.user.value)
      return html`
        <x-flex crossaxis-center center ${css({ width: '100%', height: '100vh', position: 'relative' })}>
          <ge-text> User must be logged in </ge-text>
        </x-flex>
      `;

    if (this.currStep.value === 'finish') return this.endScreen();
    return html`
      <x-flex ?horizontal=${!this.size.isTablet} ${css({ width: '100%', height: '100%', position: 'relative' })}>
        ${this.stepShower()} ${this.backgroundSVGs()}
        <x-flex
          flex
          start
          ${css({
            paddingTop: this.currStep.value === 'name' ? `calc(${this.spacing.xxl} * 3)` : this.spacing.xxl,
            paddingLeft: this.size.isTablet ? geSpacing.md : geSpacing.xxl,
            paddingRight: this.size.isTablet ? geSpacing.md : geSpacing.xxl,
          })}
        >
          <x-form
            .validator=${EntityValidator.fromEntity(UserCreationEntity)}
            .document=${this.userCreationDoc.value}
            @success=${async e => {
              if (!this.user.value) return;
              this.user.value.userData = e.detail;

              this.user.value.hasOnboarded = true;
              try {
                await this.user.value.save();
                AdornisRouter.goTo('/create_profile/finish');
              } catch (e) {
                GermaniumDialog.alert('Something went wrong');
              }
            }}
            @error=${e => {
              console.log('DOEKDPED', e);

              GermaniumDialog.alert(e.detail.message);
            }}
          >
            ${this.formContent()}
          </x-form>
        </x-flex>
      </x-flex>
    `;
  }

  private enableSubmitButton() {
    switch (this.currStep.value) {
      case 'name': {
        return !!this.userCreationDoc.value?.name;
      }
      case 'country': {
        if (!this.isInGermany)
          return (
            !!this.userCreationDoc.value?.country &&
            !!this.userCreationDoc.value?.cityInGermany &&
            !!this.userCreationDoc.value?.arrival
          );
        return !!this.userCreationDoc.value?.country && !!this.userCreationDoc.value?.cityInGermany;
      }
      case 'education': {
        if (this.userCreationDoc.value?.education === EDUCATION.OTHER)
          return !!this.userCreationDoc.value.otherEducation;
        return !!this.userCreationDoc.value?.education;
      }

      case 'study_details': {
        return (
          !!this.userCreationDoc.value?.university &&
          !!this.userCreationDoc.value?.program &&
          !!this.userCreationDoc.value?.degree
        );
      }
    }
  }

  private backgroundSVGs() {
    return html`
      ${this.size.width > 875
        ? html`
            <img
              ${css({ position: 'absolute', right: '0px', top: '140px' })}
              src=${require('client/assets/background_images/half_circle_yellow.svg')}
              style="width: 144px; height: 72px;"
            />

            <img
              src=${require('client/assets/background_images/half_circle_blue.svg')}
              ${css({ position: 'absolute', right: '500px', bottom: '0', width: '80px' })}
            />

            <img
              src=${require('client/assets/Pattern.svg')}
              ${css({ position: 'absolute', right: '0px', bottom: '350px', width: '200px' })}
            />

            ${this.size.width > 1060
              ? html`
                  <img
                    src=${require('client/assets/background_images/dots_2.svg')}
                    ${css({ position: 'absolute', right: '240px', bottom: '40px', width: '200px' })}
                  />
                `
              : nothing}

            <img
              src=${require('client/assets/background_images/half_circle_green_full.svg')}
              ${css({ position: 'absolute', right: '80px', bottom: '177px', width: '150px' })}
            />
          `
        : nothing}
    `;
  }

  @state() isInGermany = true;

  private stepShower() {
    if (!this.currStep.value) return '';
    if (this.size.isTablet)
      return html`
        <x-flex ${css({ bottom: this.spacing.xl, position: 'absolute' })}>
          <x-flex horizontal crossaxis-center space="md">
            <x-flex
              crossaxis-center
              center
              padding="sm"
              ${css({ borderRadius: '50%', backgroundColor: '#FF4B5B', marginLeft: this.spacing.md })}
            >
              <ge-h2 ${css({ color: 'white', width: '30px', textAlign: 'center' })}>
                ${this.steps.indexOf(this.currStep.value) + 1}
              </ge-h2>
            </x-flex>

            <ge-h3 ${css({ color: this.colors.accent })}>
              ${this.steps[this.steps.indexOf(this.currStep.value!)]}
            </ge-h3>
          </x-flex>
        </x-flex>
      `;
    return html`
      <x-flex
        crossaxis-start
        center
        ${css({
          width: '16%',
          minWidth: '260px',
          height: '100%',
          backgroundImage: `url('${require('client/assets/black_bg.png')}')`,
          backgroundSize: '100% 100%',
        })}
      >
        <x-flex center ${css({ height: '75%', position: 'relative' })}>
          <x-flex flex center ${css({ position: 'relative', zIndex: '3' })}>
            <x-flex horizontal crossaxis-center space="md">
              <x-flex
                crossaxis-center
                center
                padding="sm"
                ${css({
                  borderRadius: '50%',
                  backgroundColor:
                    this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('name') ? '#FF4B5B' : 'white',
                  marginLeft: geSpacing.md,
                })}
              >
                <ge-h2
                  ${css({
                    color:
                      this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('name')
                        ? 'white'
                        : this.colors.secondary,
                    width: '30px',
                    textAlign: 'center',
                    fontWeight: '600',
                  })}
                >
                  1
                </ge-h2>
              </x-flex>

              <ge-h3 ${css({ color: this.colors.primary, textDecorationLine: 'underline', fontWeight: '600' })}>
                Name
              </ge-h3>
            </x-flex>
          </x-flex>
          <x-flex flex center ${css({ position: 'relative', zIndex: '3' })}>
            <x-flex horizontal crossaxis-center space="md">
              <x-flex
                crossaxis-center
                center
                padding="sm"
                ${css({
                  borderRadius: '50%',
                  backgroundColor:
                    this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('country') ? '#FF4B5B' : 'white',
                  marginLeft: geSpacing.md,
                })}
              >
                <ge-h2
                  ${css({
                    color:
                      this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('country')
                        ? 'white'
                        : this.colors.secondary,
                    width: '30px',
                    textAlign: 'center',
                    fontWeight: '600',
                  })}
                >
                  2
                </ge-h2>
              </x-flex>
              <ge-h3 ${css({ color: this.colors.primary, textDecorationLine: 'underline', fontWeight: '600' })}>
                Country
              </ge-h3>
            </x-flex>
          </x-flex>
          <x-flex flex center ${css({ position: 'relative', zIndex: '3' })}>
            <x-flex horizontal crossaxis-center space="md">
              <x-flex
                crossaxis-center
                center
                padding="sm"
                ${css({
                  borderRadius: '50%',
                  backgroundColor:
                    this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('education') ? '#FF4B5B' : 'white',
                  marginLeft: geSpacing.md,
                })}
              >
                <ge-h2
                  ${css({
                    color:
                      this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('education')
                        ? 'white'
                        : this.colors.secondary,
                    width: '30px',
                    textAlign: 'center',
                    fontWeight: '600',
                  })}
                >
                  3
                </ge-h2>
              </x-flex>

              <ge-h3 ${css({ color: this.colors.primary, textDecorationLine: 'underline', fontWeight: '600' })}>
                Education
              </ge-h3>
            </x-flex>
          </x-flex>
          <x-flex flex center ${css({ position: 'relative', zIndex: '3' })}>
            <x-flex horizontal crossaxis-center space="md">
              <x-flex
                crossaxis-center
                center
                padding="sm"
                ${css({
                  borderRadius: '50%',
                  backgroundColor:
                    this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('study_details')
                      ? '#FF4B5B'
                      : 'white',
                  marginLeft: geSpacing.md,
                })}
              >
                <ge-h2
                  ${css({
                    fontWeight: '600',
                    color:
                      this.steps.indexOf(this.currStep.value) >= this.steps.indexOf('study_details')
                        ? 'white'
                        : this.colors.secondary,
                    width: '30px',
                    textAlign: 'center',
                  })}
                >
                  4
                </ge-h2>
              </x-flex>

              <ge-h3 ${css({ color: this.colors.primary, textDecorationLine: 'underline', fontWeight: '600' })}>
                Study details
              </ge-h3>
            </x-flex>
          </x-flex>

          <x-flex
            ${css({
              borderLeft: `3px dashed white`,
              height: '80%',
              position: 'absolute',
              left: `calc(${geSpacing.xl} - 2px)`,
              width: '2px',
              zIndex: '1',
            })}
          >
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private formContent() {
    switch (this.currStep.value) {
      case 'name':
        return html`
          <x-flex space="xl" ?crossaxis-start=${!this.size.isTablet} center>
            <ge-h1 middle ${css({ textAlign: this.size.isTablet ? 'center' : 'start' })}> What’s your name? </ge-h1>

            <x-flex horizontal crossaxis-center space="sm">
              <ge-input placeholder="Name" a-form-key="name"> </ge-input>
              <ge-button
                mode="action"
                @click=${() => {
                  if (this.enableSubmitButton()) AdornisRouter.goTo('/create_profile/country');
                  else ASnackbar.show('Data is missing.');
                }}
                ${css({ height: '100%' })}
              >
                SUBMIT
              </ge-button>
            </x-flex>
          </x-flex>
        `;

      case 'country':
        return html`
          <x-flex space="xl" ?crossaxis-start=${!this.size.isTablet} ?crossaxis-center=${this.size.isTablet}>
            <ge-h1 middle ${css({ textAlign: this.size.isTablet ? 'center' : 'start' })}> Where are you from? </ge-h1>

            <x-flex space="md">
              <x-flex ${css({ paddingBottom: this.spacing.md })}>
                <ge-country-picker a-form-key="country"> </ge-country-picker>
              </x-flex>

              <x-flex horizontal crossaxis-center space="sm">
                <ge-h4> Are you already in Germany? </ge-h4>

                <ge-toggle-button
                  .value=${this.isInGermany}
                  @value-changed=${e => {
                    if (e.detail.value !== this.isInGermany) this.isInGermany = e.detail.value;
                  }}
                ></ge-toggle-button>
              </x-flex>

              <x-flex space="md">
                ${!this.isInGermany
                  ? html`
                      <x-flex ${css({ position: 'relative' })}>
                        <ge-date-picker-dropdown a-form-key="arrival" placeholder="Date of arrival">
                        </ge-date-picker-dropdown>

                        <x-icon
                          type="material-outlined"
                          ${css({ position: 'absolute', right: this.spacing.sm, top: this.spacing.sm })}
                        >
                          calendar_month
                        </x-icon>
                      </x-flex>
                    `
                  : nothing}
                <x-flex space="sm">
                  <ge-text> Which city </ge-text>
                  <ge-input placeholder="Please enter" a-form-key="cityInGermany"> </ge-input>
                </x-flex>
              </x-flex>
              <div></div>
            </x-flex>

            <x-flex horizontal start space="sm" ${css({ paddingTop: this.spacing.md })}>
              <ge-button mode="outline" tone="error" @click=${() => window.history.back()}> BACK </ge-button>
              <ge-button
                mode="action"
                @click=${() => {
                  if (this.enableSubmitButton()) AdornisRouter.goTo('/create_profile/education');
                  else ASnackbar.show('Data is missing.');
                }}
              >
                SUBMIT
              </ge-button>
            </x-flex>
          </x-flex>
        `;

      case 'education':
        return html`
          <x-flex space="xl" ?crossaxis-start=${!this.size.isTablet} ?crossaxis-center=${this.size.isTablet}>
            <ge-h1 ${css({ textAlign: this.size.isTablet ? 'center' : 'start' })}> Are you...? </ge-h1>

            <ge-radiogroup a-form-key="education">
              <x-flex space="lg">
                <ge-radiobutton .selectable=${EDUCATION.STUDENT}> Student </ge-radiobutton>
                <ge-radiobutton .selectable=${EDUCATION.AUPAIR}> AuPair </ge-radiobutton>
                <ge-radiobutton .selectable=${EDUCATION.SPRACHSTUDENT}> Sprachstudent </ge-radiobutton>
                <ge-radiobutton .selectable=${EDUCATION.YOUNG_PROFESSIONAL}> Young Professional </ge-radiobutton>
                <x-flex space="sm">
                  <ge-radiobutton .selectable=${EDUCATION.OTHER}> Other </ge-radiobutton>
                  <x-flex ${css({ paddingLeft: this.spacing.lg })}>
                    <ge-input a-form-key="otherEducation" placeholder="Tell us..."> </ge-input>
                  </x-flex>
                </x-flex>
              </x-flex>
            </ge-radiogroup>

            <!-- <x-radiogroup a-form-key="education" @value-picked=${e => alert(e.detail.value)}>
              <x-flex space="lg">
                <x-radiobutton .selectable=${EDUCATION.STUDENT}> Student </x-radiobutton>
                <x-radiobutton .selectable=${EDUCATION.AUPAIR}> AuPair </x-radiobutton>
                <x-radiobutton .selectable=${EDUCATION.SPRACHSTUDENT}> Sprachstudent </x-radiobutton>
                <x-radiobutton .selectable=${EDUCATION.YOUNG_PROFESSIONAL}> Young Professional </x-radiobutton>
                <x-flex space="sm">
                  <x-radiobutton .selectable=${EDUCATION.OTHER}> Other </x-radiobutton>
                  <x-flex ${css({ paddingLeft: this.spacing.lg })}>
                    <ge-input a-form-key="otherEducation" placeholder="Tell us..."> </ge-input>
                  </x-flex>
                </x-flex>
              </x-flex>
            </x-radiogroup> -->

            <x-flex horizontal start space="md">
              <ge-button mode="outline" tone="error" @click=${() => window.history.back()}> BACK </ge-button>
              <ge-button
                mode="action"
                @click=${() => {
                  if (this.enableSubmitButton()) AdornisRouter.goTo('/create_profile/study_details');
                  else ASnackbar.show('Data is missing.');
                }}
              >
                SUBMIT
              </ge-button>
            </x-flex>
          </x-flex>
        `;

      case 'study_details':
        return html`
          <x-flex space="xl" ?crossaxis-start=${!this.size.isTablet} ?crossaxis-center=${this.size.isTablet}>
            <ge-h1
              ${css({
                textAlign: this.size.isTablet ? 'center' : 'start',
                fontWeight: '600',
              })}
            >
              Where do you study?
            </ge-h1>

            <x-flex space="md" ${css({ width: '100%', maxWidth: '500px' })}>
              <x-flex space="sm">
                <ge-text> University </ge-text>
                <ge-dropdown-search-selection
                  ${css({ width: '100%' })}
                  a-form-key="university"
                  .search=${(searchVal: string) => {
                    return universityList
                      .filter(
                        uni => uni.country === 'Germany' && uni.name.toLowerCase().includes(searchVal.toLowerCase()),
                      )
                      .slice(0, 10)
                      .map(e => e.name);
                  }}
                  .renderString=${val => {
                    return val || '';
                  }}
                >
                </ge-dropdown-search-selection>
              </x-flex>

              <x-flex space="sm">
                <ge-text> Program of study </ge-text>

                <ge-input a-form-key="program" placeholder="Program of study"> </ge-input>
              </x-flex>
              <x-flex space="sm">
                <ge-text> Degree </ge-text>

                <ge-dropdown-selection a-form-key="degree" .selectables=${['Bachelor', 'Master', 'PHD', 'Other']}>
                </ge-dropdown-selection>
              </x-flex>
            </x-flex>
            <x-flex horizontal start space="md">
              <ge-button mode="outline" tone="error" @click=${() => window.history.back()}> BACK </ge-button>
              <ge-button
                mode="action"
                @click=${() => {
                  if (this.enableSubmitButton()) this.shadowRoot?.querySelector<AdornisForm<any>>('x-form')?.validate();
                  else ASnackbar.show('Data is missing.');
                }}
              >
                SUBMIT
              </ge-button>
            </x-flex>
          </x-flex>
        `;
    }
  }

  private endScreen() {
    if (this.size.isMobile)
      return html`
        <x-flex
          ${css({
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: this.colors.secondary,
          })}
          padding="md"
          crossaxis-center
          center
        >
          <x-flex space="xl" crossaxis-start>
            <ge-h1 ${css({ color: this.colors.primary, fontWeight: 'bold' })}>
              Welcome to <br />
              our community!
            </ge-h1>

            <ge-text ${css({ color: 'white' })}>
              You´re only one click away from discovering all of our amazing features.
            </ge-text>

            <ge-button ${css({ boxShadow: 'none' })} @click=${() => AdornisRouter.goTo('/')}> LET'S GO </ge-button>
          </x-flex>
        </x-flex>
      `;
    return html`
      <x-flex
        ${css({
          width: '100%',
          height: '100%',
          position: 'relative',
          backgroundColor: this.colors.secondary,
        })}
      >
        <x-flex space="xl" crossaxis-start ${css({ position: 'absolute', left: '20%', top: '240px' })}>
          <ge-h1 ${css({ color: this.colors.primary, fontWeight: 'bold' })}>
            Welcome to <br />
            our community!
          </ge-h1>

          <ge-text ${css({ color: 'white' })}>
            You´re only one click away from discovering all of our amazing features.
          </ge-text>

          <ge-button ${css({ boxShadow: 'none' })} @click=${() => AdornisRouter.goTo('/')}> LET'S GO </ge-button>
        </x-flex>

        <img
          ${css({
            position: 'absolute',
            right: '35%',
            top: '100px',
            width: this.size.width < 1360 ? (this.size.width < 565 ? '80px' : '114px') : '144px',
            height: this.size.width < 1360 ? (this.size.width < 565 ? '38px' : '62px') : '72px',
          })}
          src=${require('client/assets/svgs_home/Half_Circle_1.svg')}
        />

        <img
          width="160px"
          src=${require('client/assets/svgs_home/Dots.svg')}
          ${css({ bottom: '0', right: '30%', position: 'absolute' })}
        />
        <img
          width="158px"
          src="${require('client/assets/people_home/person4.png')}"
          ${css({
            position: 'absolute',
            bottom: '100px',
            right: '40%',
            borderRadius: '50%',
          })}
        />

        <img
          width="172px"
          ${css({ right: '26%', top: '130px', position: 'absolute', borderRadius: '50%', zIndex: '4' })}
          src=${require('client/assets/people_home/person2.png')}
        />

        <img
          width="122px"
          src=${require('client/assets/green_half.svg')}
          ${css({
            position: 'absolute',
            bottom: '150px',
            right: '0',
          })}
        />

        <img
          ${css({
            position: 'absolute',
            right: '9%',
            top: '160px',
            width: '414px',
            zIndex: '2',
          })}
          src=${require('client/assets/svgs_home/Pattern.svg')}
        />
        <img
          src=${require('client/assets/people_home/person3.png')}
          ${css({
            position: 'absolute',
            bottom: '100px',
            left: '5%',
            zIndex: '5',

            width: '112px',
            borderRadius: '50%',
          })}
        />

        <img
          height="150px"
          width="72px"
          src=${require('client/assets/blue_half.svg')}
          ${css({
            position: 'absolute',
            bottom: '150px',
            left: '3%',
            zIndex: '3',
          })}
        />

        <img
          ${css({
            position: 'absolute',
            top: '140px',
            left: '-80px',
            width: '254px',
            zIndex: '2',
          })}
          src=${require('client/assets/svgs_home/Pattern.svg')}
        />

        <img
          src=${require('client/assets/people_home/person1.png')}
          ${css({
            position: 'absolute',
            top: '100px',
            left: '5%',
            width: this.size.width < 1360 ? (this.size.width < 565 ? '80px' : '120px') : '146px',
            borderRadius: '50%',
          })}
        />
      </x-flex>
    `;
  }
}
